/**
 * Modèle unifié du Journal : articles de blog + études de cas (tag « Projet »).
 * Les 4 tags sont fermés : Projet · Point de vue · Méthode · Suisse.
 */
export const JOURNAL_TAGS = ["Projet", "Point de vue", "Méthode", "Suisse"] as const;
export type JournalTag = (typeof JOURNAL_TAGS)[number];

/** Normalise une catégorie libre (CMS / IA Cara) vers l'un des 4 tags. */
export function normalizeTag(category?: string | null): JournalTag {
  const c = (category ?? "").toLowerCase();
  if (/suisse|switzerland|schweiz|svizzera/.test(c)) return "Suisse";
  if (/projet|cas|case|client|réalisation|realisation/.test(c)) return "Projet";
  if (/méthode|methode|guide|how|tuto|playbook|checklist|ressource/.test(c)) return "Méthode";
  return "Point de vue";
}


export type JournalEntry = {
  kind: "post" | "case";
  slug: string;
  title: string;
  excerpt: string | null;
  cover_url: string | null;
  cover_alt: string | null;
  tag: JournalTag;
  published_at: string | null;
  client_name: string | null;
  /** Secteur brut du cas (études de cas uniquement). */
  sector: string | null;
};

export function postToEntry(p: any): JournalEntry {
  return {
    kind: "post",
    slug: p.slug,
    title: p.title,
    excerpt: p.excerpt ?? null,
    cover_url: p.cover_url ?? null,
    cover_alt: p.cover_alt ?? null,
    tag: normalizeTag(p.category),
    published_at: p.published_at ?? null,
    client_name: null,
    sector: null,
  };
}

export function caseToEntry(c: any): JournalEntry {
  return {
    kind: "case",
    slug: c.slug,
    title: c.client ? `${c.client} — étude de cas` : c.title,
    excerpt: c.headline ?? c.summary ?? c.intro ?? null,
    cover_url: c.cover_url ?? null,
    cover_alt: c.cover_alt ?? null,
    tag: "Projet",
    published_at: c.published_at ?? c.updated_at ?? null,
    client_name: c.client ?? null,
    sector: c.industry ?? null,
  };
}

/** Fusionne et trie du plus récent au plus ancien. */
export function mergeJournal(posts: any[], cases: any[]): JournalEntry[] {
  return [...posts.map(postToEntry), ...cases.map(caseToEntry)].sort(
    (a, b) => new Date(b.published_at ?? 0).getTime() - new Date(a.published_at ?? 0).getTime(),
  );
}

/** Filtres secteur de la grille Projets (brief V2 §4.3). */
export const PROJECT_SECTORS = ["restaurants", "fitness", "mode", "commerce", "perso"] as const;
export type ProjectSector = (typeof PROJECT_SECTORS)[number];

export function sectorBucket(raw?: string | null): ProjectSector | null {
  const s = (raw ?? "").toLowerCase();
  if (!s) return null;
  if (/restau|food|café|cafe|bar|plage|hôtel|hotel|traiteur|gastro|pizz/.test(s)) return "restaurants";
  if (/fitness|sport|gym|coach|bien-être|wellness|yoga/.test(s)) return "fitness";
  if (/mode|fashion|beauté|beaute|bijou|montre|watch|cosm|luxe/.test(s)) return "mode";
  if (/personal|perso|influence|créateur|createur|artiste|talent/.test(s)) return "perso";
  return "commerce";
}
